/* global globalThis */

const EVENT_KEY_PROPERTY = "event_key";
const MAX_PROPERTY_KEY_LENGTH = 32;
const MAX_PROPERTIES_JSON_SIZE = 512;
const PDF_RENDER_IN_PROGRESS_FLAG = "__CE_PDF_RENDER_IN_PROGRESS__";
const PENDO_READY_FLAG = "__CE_PENDO_READY";
const HIGH_PRIORITY_KEYS = new Set([
  "event_key",
  "route",
  "quicklist_type",
  "source_module",
  "success",
  "error_code"
]);

const TRACK_EVENTS = {
  AUTH_LOGIN_FAILED: "Auth: Login Failed",
  AUTH_OTP_CHALLENGE_SHOWN: "Auth: OTP Challenge Shown",
  AUTH_OTP_SUCCESS: "Auth: OTP Verification Succeeded",
  AUTH_PASSWORD_RESET_SUBMIT: "Auth: Password Reset Submitted",
  AUTH_IMPERSONATION_START: "Auth: Impersonation Started",
  AUTH_IMPERSONATION_END: "Auth: Impersonation Ended",
  NAV_PATIENT_PROFILE_OPEN: "Navigation: Patient Profile Opened",
  NAV_FILTER_APPLIED: "Navigation: Filter Applied",
  NAV_FILTER_CLEARED_ALL: "Navigation: Filters Cleared",
  NAV_SEARCH_SUBMITTED: "Navigation: Search Submitted",
  QUICKLIST_PATIENT_SELECTED: "Quicklist: Patient Selected",
  QUICKLIST_CHANGE_STATUS_SUBMIT: "Quicklist: Change Status Submitted",
  QUICKLIST_NO_SHOW_SUBMIT: "Quicklist: No Show Submitted",
  QUICKLIST_RESET_STATUS_SUBMIT: "Quicklist: Reset Status Submitted",
  QUICKLIST_RECENT_VISIT_SUBMIT: "Quicklist: Recent Visit Submitted",
  QUICKLIST_SCHEDULE_APPT_SUBMIT: "Quicklist: Schedule Appointment Submitted",
  QUICKLIST_EXPORT_CSV: "Quicklist: Export CSV Submitted",
  QUICKLIST_PRINT_SUBMIT: "Quicklist: Print Submitted",
  QUICKLIST_CUSTOM_WORKLIST_SAVE: "Quicklist: Custom Worklist Saved",
  QUICKLIST_CUSTOM_WORKLIST_DELETE: "Quicklist: Custom Worklist Deleted",
  REFERRAL_SUBMIT_OPEN: "Referral: Submit Opened",
  REFERRAL_SUBMIT: "Referral: Submitted",
  REFERRAL_STATUS_UPDATE: "Referral: Status Updated",
  REFERRAL_DECLINE: "Referral: Declined",
  REFERRAL_RECEIVED_EXPORT: "Referral: Received Export Submitted",
  CM_ASSIGN_PATIENT_SUBMIT: "Care Management: Assign Patient Submitted",
  CM_MARK_OUTREACHED_SUBMIT: "Care Management: Mark Outreached Submitted",
  CM_REASSIGN_CARE_STEP_SUBMIT: "Care Management: Reassign Care Step Submitted",
  CM_ASSESSMENT_SAVE: "Care Management: Assessment Saved",
  CM_REMOVE_WORKLIST_SUBMIT: "Care Management: Remove Worklist Submitted",
  CM_SAVE_DEFAULT_VIEW: "Care Management: Default View Saved",
  CM_EXPORT_CSV: "Care Management: Export CSV Submitted",
  ADMIN_USER_CREATE: "Admin: User Created",
  ADMIN_USER_UPDATE: "Admin: User Updated",
  ADMIN_USER_ACTIVATE: "Admin: User Activated",
  ADMIN_USER_DEACTIVATE: "Admin: User Deactivated",
  ADMIN_ROLES_AUTHORITIES_SAVE: "Admin: Roles and Authorities Saved",
  ADMIN_PRACTICE_INFO_SAVE: "Admin: Practice Information Saved",
  ADMIN_REFERRAL_SETTINGS_SAVE: "Admin: Referral Settings Saved",
  ADMIN_ASSESSMENT_TEMPLATE_SAVE: "Admin: Assessment Template Saved",
  ADMIN_GAP_DOC_UPLOAD: "Admin: Gap Documentation Uploaded",
  ADMIN_SYSTEM_FILE_UPLOAD: "Admin: System File Uploaded",
  VALIDATION_ERROR: "UI: Validation Error",
  API_SUBMIT_FAILED: "API: Submit Failed",
  EXPORT_SUMMARY_VIEWED: "Export: Summary Viewed",
  FEEDBACK_SUBMIT: "Feedback: Submitted",
  UI_SORT_CHANGED: "UI: Sort Changed",
  UI_VIEW_MODE_CHANGED: "UI: View Mode Changed",
  PERF_LONG_LOAD: "Performance: Long Load Detected",
  PROFILE_INTERNAL_NOTE_SAVE: "Patient Profile: Internal Note Saved",
  PROFILE_CARE_PROGRAM_CHANGED: "Patient Profile: Care Program Changed",
  PROFILE_ASSESSMENT_SUBMIT: "Patient Profile: Assessment Submitted",
  PROFILE_HOSPITAL_EVENT_SAVE: "Patient Profile: Hospital Event Saved",
  CHART_PREP_PDF_DOWNLOAD: "Chart Prep: PDF Downloaded",
  MANAGE_VISIT_UPDATE_SUBMIT: "Manage Visit: Update Submitted",
  MANAGE_VISIT_EVIDENCE_UPLOAD: "Manage Visit: Evidence Uploaded"
};

const TRACK_EVENT_KEYS = {
  AUTH_LOGIN_FAILED: "ce_auth_login_failed",
  AUTH_OTP_CHALLENGE_SHOWN: "ce_auth_otp_challenge_shown",
  AUTH_OTP_SUCCESS: "ce_auth_otp_success",
  AUTH_PASSWORD_RESET_SUBMIT: "ce_auth_password_reset_submit",
  AUTH_IMPERSONATION_START: "ce_auth_impersonation_start",
  AUTH_IMPERSONATION_END: "ce_auth_impersonation_end",
  NAV_PATIENT_PROFILE_OPEN: "ce_nav_patient_profile_open",
  NAV_FILTER_APPLIED: "ce_nav_filter_applied",
  NAV_FILTER_CLEARED_ALL: "ce_nav_filter_cleared_all",
  NAV_SEARCH_SUBMITTED: "ce_nav_search_submitted",
  QUICKLIST_PATIENT_SELECTED: "ce_quicklist_patient_selected",
  QUICKLIST_CHANGE_STATUS_SUBMIT: "ce_quicklist_change_status_submit",
  QUICKLIST_NO_SHOW_SUBMIT: "ce_quicklist_no_show_submit",
  QUICKLIST_RESET_STATUS_SUBMIT: "ce_quicklist_reset_status_submit",
  QUICKLIST_RECENT_VISIT_SUBMIT: "ce_quicklist_recent_visit_submit",
  QUICKLIST_SCHEDULE_APPT_SUBMIT: "ce_quicklist_schedule_appt_submit",
  QUICKLIST_EXPORT_CSV: "ce_quicklist_export_csv",
  QUICKLIST_PRINT_SUBMIT: "ce_quicklist_print_submit",
  QUICKLIST_CUSTOM_WORKLIST_SAVE: "ce_quicklist_custom_worklist_save",
  QUICKLIST_CUSTOM_WORKLIST_DELETE: "ce_quicklist_custom_worklist_delete",
  REFERRAL_SUBMIT_OPEN: "ce_referral_submit_open",
  REFERRAL_SUBMIT: "ce_referral_submit",
  REFERRAL_STATUS_UPDATE: "ce_referral_status_update",
  REFERRAL_DECLINE: "ce_referral_decline",
  REFERRAL_RECEIVED_EXPORT: "ce_referral_received_export",
  CM_ASSIGN_PATIENT_SUBMIT: "ce_cm_assign_patient_submit",
  CM_MARK_OUTREACHED_SUBMIT: "ce_cm_mark_outreached_submit",
  CM_REASSIGN_CARE_STEP_SUBMIT: "ce_cm_reassign_care_step_submit",
  CM_ASSESSMENT_SAVE: "ce_cm_assessment_save",
  CM_REMOVE_WORKLIST_SUBMIT: "ce_cm_remove_worklist_submit",
  CM_SAVE_DEFAULT_VIEW: "ce_cm_save_default_view",
  CM_EXPORT_CSV: "ce_cm_export_csv",
  ADMIN_USER_CREATE: "ce_admin_user_create",
  ADMIN_USER_UPDATE: "ce_admin_user_update",
  ADMIN_USER_ACTIVATE: "ce_admin_user_activate",
  ADMIN_USER_DEACTIVATE: "ce_admin_user_deactivate",
  ADMIN_ROLES_AUTHORITIES_SAVE: "ce_admin_roles_authorities_save",
  ADMIN_PRACTICE_INFO_SAVE: "ce_admin_practice_info_save",
  ADMIN_REFERRAL_SETTINGS_SAVE: "ce_admin_referral_settings_save",
  ADMIN_ASSESSMENT_TEMPLATE_SAVE: "ce_admin_assessment_template_save",
  ADMIN_GAP_DOC_UPLOAD: "ce_admin_gap_doc_upload",
  ADMIN_SYSTEM_FILE_UPLOAD: "ce_admin_system_file_upload",
  VALIDATION_ERROR: "ce_validation_error",
  API_SUBMIT_FAILED: "ce_api_submit_failed",
  EXPORT_SUMMARY_VIEWED: "ce_export_summary_viewed",
  FEEDBACK_SUBMIT: "ce_feedback_submit",
  UI_SORT_CHANGED: "ce_ui_sort_changed",
  UI_VIEW_MODE_CHANGED: "ce_ui_view_mode_changed",
  PERF_LONG_LOAD: "ce_perf_long_load",
  PROFILE_INTERNAL_NOTE_SAVE: "ce_profile_internal_note_save",
  PROFILE_CARE_PROGRAM_CHANGED: "ce_profile_care_program_changed",
  PROFILE_ASSESSMENT_SUBMIT: "ce_profile_assessment_submit",
  PROFILE_HOSPITAL_EVENT_SAVE: "ce_profile_hospital_event_save",
  CHART_PREP_PDF_DOWNLOAD: "ce_chart_prep_pdf_download",
  MANAGE_VISIT_UPDATE_SUBMIT: "ce_manage_visit_update_submit",
  MANAGE_VISIT_EVIDENCE_UPLOAD: "ce_manage_visit_evidence_upload"
};

const EVENT_NAME_TO_KEY = Object.keys(TRACK_EVENTS).reduce((acc, eventKey) => {
  acc[TRACK_EVENTS[eventKey]] = TRACK_EVENT_KEYS[eventKey];
  return acc;
}, {});

const deriveEventKeyFromEventName = (eventName) => {
  if (EVENT_NAME_TO_KEY[eventName]) {
    return EVENT_NAME_TO_KEY[eventName];
  }

  const normalized = String(eventName || "")
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "_")
    .replaceAll(/_+/g, "_")
    .replaceAll(/^_+|_+$/g, "");

  return normalized || null;
};

const sanitizeKey = (key) => {
  if (!key) return null;
  const safe = String(key)
    .replaceAll(/\W/g, "_")
    .replaceAll(/_+/g, "_")
    .replaceAll(/^_+|_+$/g, "");
  if (!safe) return null;
  return safe.slice(0, MAX_PROPERTY_KEY_LENGTH);
};

const safeSerializeObject = (value) => {
  const seen = new WeakSet();
  return JSON.stringify(value, (key, nestedValue) => {
    if (typeof nestedValue === "bigint") {
      return nestedValue.toString();
    }
    if (typeof nestedValue === "object" && nestedValue !== null) {
      if (seen.has(nestedValue)) {
        return "[Circular]";
      }
      seen.add(nestedValue);
    }
    return nestedValue;
  });
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim() || null;
  if (Array.isArray(value)) {
    const flattened = value
      .map((item) => normalizeValue(item))
      .filter(Boolean)
      .join(",");
    return flattened || null;
  }
  if (typeof value === "object") {
    const serialized = safeSerializeObject(value);
    return serialized === "{}" ? null : serialized;
  }
  return null;
};

const trimToPendoSize = (properties) => {
  const result = { ...properties };
  const jsonSize = () => {
    const serialized = safeSerializeObject(result);
    return serialized ? serialized.length : MAX_PROPERTIES_JSON_SIZE + 1;
  };

  while (jsonSize() > MAX_PROPERTIES_JSON_SIZE) {
    const removableKeys = Object.keys(result).filter((key) => !HIGH_PRIORITY_KEYS.has(key));
    if (!removableKeys.length) {
      break;
    }
    removableKeys.sort((a, b) => String(result[b] || "").length - String(result[a] || "").length);
    const keyToRemove = removableKeys[0];
    delete result[keyToRemove];
  }

  return result;
};

const buildProperties = (eventName, rawProperties = {}) => {
  const normalized = {};
  Object.entries(rawProperties || {}).forEach(([rawKey, rawValue]) => {
    const key = sanitizeKey(rawKey);
    if (!key) return;
    const value = normalizeValue(rawValue);
    if (value === null) return;
    normalized[key] = typeof value === "string" ? value.slice(0, 128) : value;
  });

  if (!normalized[EVENT_KEY_PROPERTY]) {
    normalized[EVENT_KEY_PROPERTY] = deriveEventKeyFromEventName(eventName);
  }
  if (!normalized.route && globalThis.window !== undefined) {
    normalized.route = globalThis.window?.location?.pathname || "/";
  }

  return trimToPendoSize(normalized);
};

const trackPendoEvent = (eventName, properties = {}) => {
  if (!eventName) return;
  if (globalThis.window === undefined) return;
  if (globalThis.window[PDF_RENDER_IN_PROGRESS_FLAG] && eventName !== TRACK_EVENTS.CHART_PREP_PDF_DOWNLOAD) return;
  if (!globalThis.window[PENDO_READY_FLAG]) return;

  const pendo = globalThis.window?.pendo;
  if (!pendo || typeof pendo.track !== "function") return;

  try {
    pendo.track(eventName, buildProperties(eventName, properties));
  } catch (error) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn("Pendo tracking failed", { eventName, error });
    }
  }
};

const getErrorCode = (error, fallback = "unknown_error") => {
  return (
    error?.response?.data?.code ||
    error?.response?.data?.errorCode ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

export { TRACK_EVENTS, TRACK_EVENT_KEYS, trackPendoEvent, getErrorCode };
